import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherAlertTriangle } from "@subframe/core";
import { format } from "date-fns";
import type { ReactNode } from "react";

import { EntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import type { FollowUpActionResponseDto } from "@/generated/model/followUpActionResponseDto";
import type { SortDir } from "@/lib/category-display";
import { asCategoryType, categoryColor } from "@/lib/category-display";
import {
  formatActionDueDate,
  getActionStatusBadge,
  isActionOverdue,
} from "@/lib/follow-up-action-utils";
import {
  type FollowUpActionSortColumn,
  sourceTypeLabel,
} from "@/lib/follow-up-actions/follow-up-actions-list";

interface FollowUpActionsTableProps {
  items: FollowUpActionResponseDto[];
  categories: CategoryResponseDto[];
  /** History shows the completed date/time in the Due column instead of the due date. */
  isHistory: boolean;
  activeColumn?: FollowUpActionSortColumn;
  activeDir: SortDir;
  onSort: (column: FollowUpActionSortColumn) => void;
  onView: (id: string) => void;
  /** Optional per-row overflow menu (context actions), rendered beside View. */
  renderRowMenu?: (action: FollowUpActionResponseDto) => ReactNode;
}

/**
 * The action row's entity glyph, tinted by its primary category. Falls back to a
 * neutral warning triangle when the category slug isn't part of the fixed icon
 * matrix (an admin-created category) or the action has no category.
 */
function ActionEntityIcon({
  slug,
  color,
  size = 32,
}: {
  slug?: string;
  color?: string;
  size?: number;
}) {
  const category = asCategoryType(slug);
  if (category) {
    return (
      <EntityIcon entity="follow-up-action" category={category} size={size} />
    );
  }
  const tint = categoryColor(slug, color);
  return (
    <span
      className="inline-flex items-center justify-center rounded-md"
      style={{
        width: size,
        height: size,
        backgroundColor: tint ? `${tint}1A` : "#F1F1F1",
        color: tint ?? "#6B7280",
      }}
    >
      <FeatherAlertTriangle style={{ width: size * 0.5, height: size * 0.5 }} />
    </span>
  );
}

export function FollowUpActionsTable({
  items,
  categories,
  isHistory,
  activeColumn,
  activeDir,
  onSort,
  onView,
  renderRowMenu,
}: FollowUpActionsTableProps) {
  const categoryById = new Map(categories.map((c) => [c.id, c]));

  return (
    <Table
      className={TABLE_MIN_WIDTH.lg}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="action"
            label="Action"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="type"
            label="Type"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="dueDate"
            label="Due"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="assignee"
            label="Assignee"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="status"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {items.map((action) => {
        const category = action.primaryCategoryId
          ? categoryById.get(action.primaryCategoryId)
          : undefined;
        const categoryTint = categoryColor(
          category?.slug,
          category?.color ?? undefined,
        );
        const overdue = isActionOverdue(action);
        const assigneeName = action.assignee
          ? `${action.assignee.firstName ?? ""} ${action.assignee.lastName ?? ""}`.trim() ||
            action.assignee.email
          : (action.assigneeRole ?? "—");
        const relative = formatActionDueDate(action);

        return (
          <Table.Row
            key={action.id}
            clickable
            onClick={() => onView(action.id)}
          >
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <ActionEntityIcon
                  slug={category?.slug}
                  color={category?.color ?? undefined}
                />
                <span className="text-body-bold font-body-bold text-neutral-900">
                  {action.description}
                </span>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {category ? (
                <span
                  className="text-body-bold font-body-bold"
                  style={categoryTint ? { color: categoryTint } : undefined}
                >
                  {category.name}
                </span>
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <Badge variant="neutral">
                {sourceTypeLabel(action.sourceType)}
              </Badge>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {isHistory ? (
                <span className="text-caption font-caption text-neutral-900">
                  {action.completedAt
                    ? format(new Date(action.completedAt), "d MMM yyyy, h:mm a")
                    : "—"}
                </span>
              ) : (
                <div className="flex flex-col items-start">
                  <span className="text-caption font-caption text-neutral-900">
                    {action.dueDate
                      ? format(new Date(action.dueDate), "d MMM yyyy")
                      : "No due date"}
                  </span>
                  {relative && (
                    <span
                      className={`text-caption font-caption ${
                        overdue ? "text-error-600" : "text-subtext-color"
                      }`}
                    >
                      {overdue ? "Overdue" : relative}
                    </span>
                  )}
                </div>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {assigneeName}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {getActionStatusBadge(action.status)}
            </Table.Cell>
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="neutral-primary"
                  onClick={() => onView(action.id)}
                >
                  View
                </Button>
                {renderRowMenu?.(action)}
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
